import { useEffect } from 'react'

import { RiskBadge, STATUS_HINT, StatusBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Controls'
import { ErrorState, Skeleton } from '@/components/ui/States'
import { useExecutionDetail } from '@/features/executions/useExecutions'
import { formatMs, formatTimestamp } from '@/lib/utils/format'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-line/60 py-2
        sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-3"
    >
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-ink-dim">{children}</dd>
    </div>
  )
}

/**
 * One execution, in full.
 *
 * Note what is not here: the arguments. The telemetry table stores a digest of them on
 * purpose, so issue bodies never end up in an operational log. The digest still answers
 * "did the same call repeat?", which is what the audit trail is for.
 */
export function ExecutionDrawer({
  requestId,
  onClose,
}: {
  requestId: string | null
  onClose: () => void
}) {
  const { data, isLoading, error, refetch } = useExecutionDetail(requestId)

  useEffect(() => {
    if (!requestId) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [requestId, onClose])

  if (!requestId) return null

  return (
    <aside
      // Below `lg` the drawer sits under the table rather than beside it, so the rule
      // that separates them belongs on top.
      className="flex h-full w-full flex-col rounded border border-line bg-surface
        lg:w-[26rem] lg:rounded-none lg:border-0 lg:border-l"
      aria-label="Execution detail"
    >
      <header className="panel-header">
        <h2 className="mono truncate text-ink">{data?.tool_name ?? 'Execution'}</h2>
        <Button variant="ghost" size="sm" onClick={onClose} title="Close (Esc)">
          Close
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
        {isLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-6" />
            ))}
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : data ? (
          <dl>
            <Field label="Status">
              <span className="flex items-center gap-2">
                <StatusBadge status={data.status} />
                <span className="text-xs text-ink-muted">{STATUS_HINT[data.status]}</span>
              </span>
            </Field>
            <Field label="Risk">
              <RiskBadge risk={data.operation_type as 'READ' | 'WRITE' | 'DESTRUCTIVE' | 'META'} />
            </Field>
            <Field label="Started">{formatTimestamp(data.created_at)}</Field>
            <Field label="Total latency">
              <span className="mono">{formatMs(data.latency_ms)}</span>
            </Field>
            <Field label="GitHub latency">
              <span className="mono">{formatMs(data.github_latency_ms)}</span>
              {data.github_latency_ms != null && data.latency_ms > 0 ? (
                <span className="ml-2 text-xs text-ink-muted">
                  {Math.round((data.github_latency_ms / data.latency_ms) * 100)}% of the call
                </span>
              ) : null}
            </Field>
            <Field label="Repository">
              <span className="mono">{data.repository ?? '—'}</span>
            </Field>
            {data.error_type ? (
              <Field label="Error">
                <span className="mono text-fail">{data.error_type}</span>
                {data.external_status_code ? (
                  <span className="mono ml-2 text-ink-muted">
                    GitHub {data.external_status_code}
                  </span>
                ) : null}
                {data.error_message ? (
                  <p className="mt-1 text-sm text-ink-dim">{data.error_message}</p>
                ) : null}
              </Field>
            ) : null}
            <Field label="Request">
              <span className="mono break-all">{data.request_id}</span>
            </Field>
            <Field label="Principal">
              <span className="mono">{data.principal ?? '—'}</span>
            </Field>
            <Field label="Arguments">
              <span className="mono break-all text-ink-muted">
                {data.arguments_digest ? `sha256:${data.arguments_digest.slice(0, 24)}…` : '—'}
              </span>
              <p className="mt-1 text-xs text-ink-faint">
                A digest, not the arguments. Issue bodies are never written to the telemetry
                table.
              </p>
            </Field>
          </dl>
        ) : null}
      </div>
    </aside>
  )
}
